// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../store/AppContext.js';
import { formatAmount, INCOME_TYPES, EXPENSE_TYPES } from '../../lib/eventLog.js';
import { getExchangeRate, convertCurrency } from '../../lib/currency-math.js';
import { getCurrencyDecimalPlaces } from '../../lib/currencies.js';

export function IncomeStatement() {
  const { t } = useTranslation(['reports', 'common']);
  const { state } = useApp();
  const { ledger } = state;
  const homeCurrency = ledger.settings.homeCurrency;

  const now = new Date();
  const [dateFrom, setDateFrom] = useState(`${now.getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(now.toISOString().slice(0, 10));

  const allPrices = useMemo(() => [...ledger.prices.values()], [ledger.prices]);

  const { incomeRows, expenseRows, totalIncome, totalExpenses, hasForeign } = useMemo(() => {
    const incomeMap = new Map<string, number>();
    const expenseMap = new Map<string, number>();
    let hasForeign = false;

    for (const tx of ledger.transactions.values()) {
      if (tx.voided) continue;
      if (tx.date < dateFrom || tx.date > dateTo) continue;
      for (const split of tx.splits) {
        const account = ledger.accounts.get(split.accountId);
        if (!account) continue;
        const cur = account.currency;
        let homeAmount = split.amount;
        if (cur !== homeCurrency) {
          hasForeign = true;
          const rate = getExchangeRate(cur, homeCurrency, tx.date, allPrices);
          if (rate !== null) {
            homeAmount = convertCurrency(
              split.amount, cur, homeCurrency, rate,
              getCurrencyDecimalPlaces(cur), getCurrencyDecimalPlaces(homeCurrency),
            );
          }
        }
        if (INCOME_TYPES.has(account.type)) {
          incomeMap.set(account.id, (incomeMap.get(account.id) ?? 0) + homeAmount);
        } else if (EXPENSE_TYPES.has(account.type)) {
          expenseMap.set(account.id, (expenseMap.get(account.id) ?? 0) + homeAmount);
        }
      }
    }

    const incomeRows = [...incomeMap.entries()]
      .map(([id, amount]) => ({ name: ledger.accounts.get(id)?.name ?? id, amount }))
      .sort((a, b) => a.name.localeCompare(b.name));
    const expenseRows = [...expenseMap.entries()]
      .map(([id, amount]) => ({ name: ledger.accounts.get(id)?.name ?? id, amount }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const totalIncome = incomeRows.reduce((sum, r) => sum + Math.abs(r.amount), 0);
    const totalExpenses = expenseRows.reduce((sum, r) => sum + r.amount, 0);

    return { incomeRows, expenseRows, totalIncome, totalExpenses, hasForeign };
  }, [ledger.transactions, ledger.accounts, dateFrom, dateTo, allPrices, homeCurrency]);

  const netIncome = totalIncome - totalExpenses;

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center gap-4 mb-4">
        <h2 className="text-md font-semibold text-text">{t('reports:titles.incomeStatement')}</h2>
        <div className="flex items-center gap-3 ml-auto">
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-muted">{t('reports:controls.from')}</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-muted">{t('reports:controls.to')}</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none" />
          </div>
          <button onClick={() => window.print()}
            className="px-3 py-1 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors no-print">
            {t('reports:controls.print')}
          </button>
        </div>
      </div>

      <div className="bg-bg-surface text-text border border-border max-w-xl print:bg-white print:text-gray-900 print:border-gray-200 print-full">
        <div className="px-4 py-3 border-b border-border print:border-gray-200">
          <h3 className="text-base font-bold text-text print:text-gray-900">{t('reports:titles.incomeStatement')}</h3>
          <p className="text-xs text-text-muted mt-0.5 print:text-gray-500">
            {t('reports:controls.dateRange', { from: dateFrom, to: dateTo })}
            {hasForeign && ` · ${t('reports:footnotes.allAmountsIn', { homeCurrency })}`}
          </p>
        </div>

        <div>
          <div className="px-4 py-1.5 border-b border-border-strong bg-bg-elevated print:border-gray-300 print:bg-gray-50">
            <h4 className="text-sm font-semibold text-text-muted uppercase tracking-wide print:text-gray-700">{t('reports:sections.income')}</h4>
          </div>
          <table className="w-full">
            <tbody>
              {incomeRows.length === 0 && (
                <tr><td colSpan={2} className="py-3 text-center text-sm text-text-subtle print:text-gray-400">{t('reports:empty.noIncome')}</td></tr>
              )}
              {incomeRows.map((r) => (
                <tr key={r.name} className="border-b border-border print:border-gray-100">
                  <td className="py-1.5 px-4 text-sm text-text-muted print:text-gray-700">{r.name}</td>
                  <td className="py-1.5 px-4 text-right text-sm font-mono text-positive print:text-green-700">{formatAmount(Math.abs(r.amount), homeCurrency)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-border-strong print:border-gray-300">
              <tr>
                <td className="py-1.5 px-4 text-sm font-semibold text-text-muted print:text-gray-700">{t('reports:sections.totalIncome')}</td>
                <td className="py-1.5 px-4 text-right text-sm font-mono font-semibold text-positive print:text-green-700">{formatAmount(totalIncome, homeCurrency)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="border-t border-border print:border-gray-200">
          <div className="px-4 py-1.5 border-b border-border-strong bg-bg-elevated print:border-gray-300 print:bg-gray-50">
            <h4 className="text-sm font-semibold text-text-muted uppercase tracking-wide print:text-gray-700">{t('reports:sections.expenses')}</h4>
          </div>
          <table className="w-full">
            <tbody>
              {expenseRows.length === 0 && (
                <tr><td colSpan={2} className="py-3 text-center text-sm text-text-subtle print:text-gray-400">{t('reports:empty.noExpenses')}</td></tr>
              )}
              {expenseRows.map((r) => (
                <tr key={r.name} className="border-b border-border print:border-gray-100">
                  <td className="py-1.5 px-4 text-sm text-text-muted print:text-gray-700">{r.name}</td>
                  <td className="py-1.5 px-4 text-right text-sm font-mono text-negative print:text-red-700">{formatAmount(r.amount, homeCurrency)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-border-strong print:border-gray-300">
              <tr>
                <td className="py-1.5 px-4 text-sm font-semibold text-text-muted print:text-gray-700">{t('reports:sections.totalExpenses')}</td>
                <td className="py-1.5 px-4 text-right text-sm font-mono font-semibold text-negative print:text-red-700">{formatAmount(totalExpenses, homeCurrency)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <table className="w-full border-t-2 border-border-strong print:border-gray-300">
          <tbody>
            <tr>
              <td className="py-2 px-4 text-base font-bold text-text print:text-gray-900">{t('reports:sections.netIncome')}</td>
              <td className={`py-2 px-4 text-right text-base font-mono font-bold ${netIncome >= 0 ? 'text-positive print:text-green-700' : 'text-negative print:text-red-700'}`}>
                {formatAmount(netIncome, homeCurrency)}
              </td>
            </tr>
          </tbody>
        </table>

        {hasForeign && (
          <p className="px-4 py-2 text-xs text-text-subtle border-t border-border print:text-gray-400 print:border-gray-100">
            * {t('reports:footnotes.incomeStatementConversion', { homeCurrency })}
          </p>
        )}
      </div>
    </div>
  );
}
